import React, { useRef } from 'react'

import {
  OmButton,
  OmCheckbox,
  OmForm,
  OmFormItem,
  OmInput,
  OmMenu,
  OmSelect,
  OmTooltip,
} from '@owma/kit'
import type {
  OmCheckboxProps,
  OmFormInstance,
  OmMenuItem,
  OmMenuProps,
  OmMenuRef,
  OmRule,
  OmSelectProps,
} from '@owma/kit'

import i18n from 'i18n'

import parse from 'html-react-parser'

import classNames from 'classnames'

import {
  confirmBase,
  // dragPopup
} from 'utils/ui'

import { FontFamilyMap, FontSizeMap, TextColorMap, BackColorMap } from 'popups/Settings/def'

import chevronDown24 from 'assets/images/toolbar-chevron-down.svg'

import toolbarBold from 'assets/images/toolbar-composer-style-bold.svg'
import toolbarItalic from 'assets/images/toolbar-composer-style-italic.svg'
import toolbarUnderline from 'assets/images/toolbar-composer-style-underline.svg'
import toolbarStrikeout from 'assets/images/toolbar-composer-style-strikeout.svg'
import toolbarClear from 'assets/images/toolbar-composer-style-clear.svg'

import toolbarNumberedList from 'assets/images/toolbar-composer-numbered-list.svg'
import toolbarBulletedList from 'assets/images/toolbar-composer-bulleted-list.svg'
import toolbarIndentIncrease from 'assets/images/toolbar-composer-indent-increase.svg'
import toolbarIndentDecrease from 'assets/images/toolbar-composer-indent-decrease.svg'
import toolbarLine from 'assets/images/toolbar-composer-line.svg'

import toolbarAlignLeft from 'assets/images/toolbar-composer-alignment-left.svg'
import toolbarAlignCenter from 'assets/images/toolbar-composer-alignment-center.svg'
import toolbarAlignRight from 'assets/images/toolbar-composer-alignment-right.svg'

import toolbarInsertImage from 'assets/images/toolbar-composer-insert-image.svg'
import toolbarInsertTable from 'assets/images/toolbar-composer-table.svg'
import toolbarLink from 'assets/images/toolbar-composer-link.svg'
import toolbarQuote from 'assets/images/toolbar-composer-quote.svg'

import toolbarLtr from 'assets/images/toolbar-composer-ltr.svg'
import toolbarRtl from 'assets/images/toolbar-composer-rtl.svg'

import styles0 from './style.module.scss'

// Type definitions
interface StylesType {
  [key: string]: string
}

interface OptionType {
  label: string
  value: string
}

interface ColorOptionInfo {
  index: number
  [key: string]: any
}

interface EditorApis {
  addLink: (url: string, display: string) => void
  removeLink: () => void
  focus: () => void
}

interface InsertLinkValues {
  display: string
  url: string
}

interface ModalObj {
  destroy: () => void
  update: (config: any) => void
  updateBase?: (config: any) => void
}

interface MoreMenuProps {
  items: OmMenuItem[]
}

interface DefaultValuesType {
  [key: string]: string | boolean
}

const commonSelectAttrs: Partial<OmSelectProps<string>> = {
  popupMatchSelectWidth: false,
  suffixIcon: <img src={chevronDown24} alt="chevron-down" />,
  variant: 'borderless',
}

const defaultValues: DefaultValuesType = {}

function propsToOptions(props: Record<string, string>): OptionType[] {
  return Object.keys(props).map((key) => {
    return {
      label: key,
      value: props[key],
    }
  })
}

const fontList = FontFamilyMap

export function getFontFamilyElem(styles: StylesType): React.JSX.Element {
  const options = propsToOptions(fontList)

  defaultValues.fontname = fontList['Arial']

  const attrs: OmSelectProps<string> = {
    ...commonSelectAttrs,
    className: classNames(styles.fontFamily),
    tabIndex: -1,
    options,
  }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.fontFamily')}>
      <OmFormItem name="fontname">
        <OmSelect {...attrs} />
      </OmFormItem>
    </OmTooltip>
  )
}

const fontSizeList = FontSizeMap

export function getFontSizeElem(styles: StylesType): React.JSX.Element {
  const options = propsToOptions(fontSizeList)

  defaultValues.fontsize = fontSizeList['11pt']

  const attrs: OmSelectProps<string> = {
    ...commonSelectAttrs,
    className: classNames(styles.fontSize),
    tabIndex: -1,
    options,
  }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.fontSize')}>
      <OmFormItem name="fontsize">
        <OmSelect {...attrs} />
      </OmFormItem>
    </OmTooltip>
  )
}

const textColorList = TextColorMap

const colorOptionRender = (
  styles: StylesType,
  option: OptionType,
  _info: ColorOptionInfo,
  i18nPrefix: string | null,
): React.JSX.Element => {
  // console.log('option - ', option, info)

  let label = option.label
  if (i18nPrefix != null) {
    /* i18next-extract-disable-next-line */
    label = i18n.t(`${i18nPrefix}.${label}`)
  }

  return (
    <div className={styles.colorLabel}>
      <div className={styles.colorRect} style={{ backgroundColor: option.value }} />
      <div className={styles.colorText}>{label}</div>
    </div>
  )
}

export function getTextColorElem(styles: StylesType): React.JSX.Element {
  const options = propsToOptions(textColorList)

  defaultValues.forecolor = textColorList['black']

  const svgText =
    '<svg fill=none height=24 viewBox="0 0 24 24"width=24 xmlns=http://www.w3.org/2000/svg><g id="toolbar-composer-text-color-tint-applied 1"><g id=toolbar-composer-text-color-tint-applied><path d="M17.5 19C18.8807 19 20 17.8807 20 16.5C20 15.1193 18.8807 14 17.5 14C16.1193 14 15 15.1193 15 16.5C15 17.8807 16.1193 19 17.5 19Z"id=Oval fill=currentColor /><g id=icon><g id="Mask group"><mask height=20 id=mask0_711_354 maskUnits=userSpaceOnUse style=mask-type:luminance width=20 x=2 y=2><g id=Group><path d="M22 2V22H2V2H22ZM17 12C14.7909 12 13 13.7909 13 16C13 18.2091 14.7909 20 17 20C19.2091 20 21 18.2091 21 16C21 13.7909 19.2091 12 17 12Z"id=Vector fill=white /></g></mask><g mask=url(#mask0_711_354)><g id=Group-2><g id=Group_2><g id=Group_3><path d="M6.5 18.25L11.5346 5.43459C11.6356 5.17757 11.9258 5.05107 12.1828 5.15204C12.3122 5.20286 12.4146 5.30523 12.4654 5.43459L17.5 18.25"id=Path-10 stroke=black stroke-width=1.2 stroke-linecap=round stroke-linejoin=round /><path d="M8.33334 14.25H16.0438"id=Path-12 stroke=black stroke-width=1.2 /></g></g></g></g></g></g></g></g></svg>'

  const svgElem = parse(svgText)

  const attrs: OmSelectProps<string> = {
    ...commonSelectAttrs,
    popupClassName: classNames(styles.colorPopup, styles.round),
    className: classNames(styles.textColor),
    labelRender: (info) => {
      return (
        <div className={classNames(styles.iconSelect)} style={{ color: info.value }}>
          {svgElem}
        </div>
      )
    },
    optionRender: (option, info) => {
      return colorOptionRender(
        styles,
        { label: String(option.label), value: option.value },
        info,
        'editor.textColor',
      )
    },
    tabIndex: -1,
    options,
  }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.textColor')}>
      <OmFormItem name="forecolor">
        <OmSelect {...attrs} />
      </OmFormItem>
    </OmTooltip>
  )
}

const backColorList = BackColorMap

export function getBackColorElem(styles: StylesType): React.JSX.Element {
  const options = propsToOptions(backColorList)

  defaultValues.backcolor = backColorList['white']

  const svgText =
    '<svg fill=none height=24 viewBox="0 0 24 24" width=24 xmlns=http://www.w3.org/2000/svg><g id="toolbar-composer-text-bg-color-applied 1"><g id=toolbar-composer-text-bg-color-applied><g id=Group-2><g id=Group><g id=Path-17><path d="M15.3117 9.70056L9.40778 3.79666"id=Path-16 stroke=#262626 stroke-linecap=round stroke-linejoin=round stroke-width=1.2 /></g><path d="M11.3078 5.67892L17.2789 11.65C17.8864 12.2575 17.8864 13.2425 17.2789 13.8499L12.4077 18.7211C11.8002 19.3286 10.8153 19.3286 10.2078 18.7211L5.33665 13.8499C4.72916 13.2425 4.72916 12.2575 5.33665 11.65L11.3078 5.67892Z"id=Rectangle clip-rule=evenodd fill-rule=evenodd stroke=#262626 stroke-linecap=round stroke-linejoin=round stroke-width=1.2 /></g></g><path d="M19.35 19.75C20.3855 19.75 21.225 18.9105 21.225 17.875C21.225 17.1846 20.6 16.143 19.35 14.75C18.1 16.143 17.475 17.1846 17.475 17.875C17.475 18.9105 18.3145 19.75 19.35 19.75Z"id=Oval clip-rule=evenodd fill-rule=evenodd fill=currentColor /><path d="M6.46359 12.75H16.1768L11.663 17.2824C11.4681 17.4781 11.1515 17.4788 10.9559 17.2839L10.9535 17.2815L6.46359 12.75Z"id=Rectangle_2 clip-rule=evenodd fill-rule=evenodd fill=currentColor /></g></g></svg>'

  const svgElem = parse(svgText)

  const attrs: OmSelectProps<string> = {
    ...commonSelectAttrs,
    popupClassName: classNames(styles.colorPopup, styles.rect),
    className: classNames(styles.backColor),
    labelRender: (info) => {
      return (
        <div className={classNames(styles.iconSelect)} style={{ color: info.value }}>
          {svgElem}
        </div>
      )
    },
    optionRender: (option, info) => {
      return colorOptionRender(
        styles,
        { label: String(option.label), value: option.value },
        info,
        'editor.backColor',
      )
    },
    tabIndex: -1,
    options,
  }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.backColor')}>
      <OmFormItem name="backcolor">
        <OmSelect {...attrs} />
      </OmFormItem>
    </OmTooltip>
  )
}

export function getBoldElem(styles: StylesType): React.JSX.Element {
  defaultValues.bold = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.bold')}>
      <OmFormItem
        name="bold"
        className={classNames(styles.iconCheckbox, styles.bold)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarBold} alt="toolbar-bold" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getItalicElem(styles: StylesType): React.JSX.Element {
  defaultValues.italic = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.italic')}>
      <OmFormItem
        name="italic"
        className={classNames(styles.iconCheckbox, styles.italic)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarItalic} alt="toolbar-italic" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getStrikeElem(styles: StylesType): React.JSX.Element {
  defaultValues.strikethrough = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.strike')}>
      <OmFormItem
        name="strikethrough"
        className={classNames(styles.iconCheckbox, styles.strike)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarStrikeout} alt="toolbar-strikeout" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getUnderlineElem(styles: StylesType): React.JSX.Element {
  defaultValues.underline = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.underline')}>
      <OmFormItem
        name="underline"
        className={classNames(styles.iconCheckbox, styles.underline)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarUnderline} alt="toolbar-underline" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getClearElem(styles: StylesType): React.JSX.Element {
  defaultValues.removeformat = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.clear')}>
      <OmFormItem
        name="removeformat"
        className={classNames(styles.iconCheckbox, styles.notCheckbox, styles.clear)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarClear} alt="toolbar-clear" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getNumberedListElem(styles: StylesType): React.JSX.Element {
  defaultValues.orderedlist = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.numberedList')}>
      <OmFormItem
        name="orderedlist"
        className={classNames(styles.iconCheckbox, styles.numberedList)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarNumberedList} alt="toolbar-numbered-list" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getBulletedListElem(styles: StylesType): React.JSX.Element {
  defaultValues.unorderedlist = false
  const attrs: OmCheckboxProps = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.bulletedList')}>
      <OmFormItem
        name="unorderedlist"
        className={classNames(styles.iconCheckbox, styles.bulletedList)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarBulletedList} alt="toolbar-bulleted-list" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getIndentIncreaseElem(styles: StylesType): React.JSX.Element {
  defaultValues.indent = false

  const attrs: OmCheckboxProps = {
    tabIndex: -1,
  }

  return (
    <OmTooltip title={i18n.t('editor.tooltip.indentInc')}>
      <OmFormItem
        name="indent"
        className={classNames(styles.iconCheckbox, styles.notCheckbox, styles.indentIncrease)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarIndentIncrease} alt="toolbar-indent-increase" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getIndentDecreaseElem(styles: StylesType): React.JSX.Element {
  defaultValues.outdent = false

  const attrs: OmCheckboxProps = {
    tabIndex: -1,
  }

  return (
    <OmTooltip title={i18n.t('editor.tooltip.indentDec')}>
      <OmFormItem
        name="outdent"
        className={classNames(styles.iconCheckbox, styles.notCheckbox, styles.indentDecrease)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarIndentDecrease} alt="toolbar-indent-decrease" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

// export function getLineElem() {
//   defaultValues.insertstyledhr = false

//   return (
//     <OmFormItem
//       name="insertstyledhr"
//       className={classNames(styles.iconCheckbox, styles.line)}
//       valuePropName="checked"
//       getValueFromEvent={(e) => {
//         return e.target.value
//       }}
//     >
//       <Checkbox>
//         <img src={toolbarLine} alt="toolbar-line" />
//       </OmCheckbox>
//     </OmFormItem>
//   )
// }

const hrList: Record<string, string> = {
  Solid: 'solid',
  Dotted: 'dotted',
  Dashed: 'dashed',
}

const lineOptionRender = (
  styles: StylesType,
  option: OptionType,
  _info: ColorOptionInfo,
  i18nPrefix: string | null,
): React.JSX.Element => {
  // console.log('option - ', option, info)

  let label = option.label
  if (i18nPrefix != null) {
    /* i18next-extract-disable-next-line */
    label = i18n.t(`${i18nPrefix}.${label}`)
  }

  return (
    <div className={styles.lineLabel}>
      <div className={styles.lineRect}>
        <div className={styles.line} style={{ borderTopStyle: option.value as any }} />
      </div>
      <div className={styles.lineText}>{label}</div>
    </div>
  )
}

export function getLineElem(styles: StylesType): React.JSX.Element {
  defaultValues.insertstyledhr = false

  const options = propsToOptions(hrList)

  const attrs: OmSelectProps<string> = {
    popupMatchSelectWidth: false,
    suffixIcon: null,
    variant: 'borderless',
    popupClassName: classNames(styles.linePopup, styles.styledHr),
    className: classNames(styles.hr),
    labelRender: () => {
      return <img src={toolbarLine} alt="toolbar-line" />
    },
    optionRender: (option, info) => {
      return lineOptionRender(
        styles,
        { label: String(option.label), value: option.value },
        info,
        'editor.hrStyle',
      )
    },
    onClick: (e) => {
      e.stopPropagation()
    },
    tabIndex: -1,
    options,
  }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.line')}>
      <OmFormItem name="insertstyledhr">
        <OmSelect {...attrs} />
      </OmFormItem>
    </OmTooltip>
  )
}

export function getAlignLeftElem(styles: StylesType): React.JSX.Element {
  defaultValues.alignleft = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.alignLeft')}>
      <OmFormItem
        name="alignleft"
        className={classNames(styles.iconCheckbox, styles.alignLeft)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarAlignLeft} alt="toolbar-align-left" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getAlignCenterElem(styles: StylesType): React.JSX.Element {
  defaultValues.aligncenter = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.alignCenter')}>
      <OmFormItem
        name="aligncenter"
        className={classNames(styles.iconCheckbox, styles.alignCenter)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarAlignCenter} alt="toolbar-align-center" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getAlignRightElem(styles: StylesType): React.JSX.Element {
  defaultValues.alignright = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.alignRight')}>
      <OmFormItem
        name="alignright"
        className={classNames(styles.iconCheckbox, styles.alignRight)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarAlignRight} alt="toolbar-align-right" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getInsertImageElem(styles: StylesType): React.JSX.Element {
  defaultValues.insertimage = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.insertImage')}>
      <OmFormItem
        name="insertimage"
        className={classNames(styles.iconCheckbox, styles.notCheckbox, styles.insertImage)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarInsertImage} alt="toolbar-insert-image" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getInsertTableElem(styles: StylesType): React.JSX.Element {
  defaultValues.insertimage = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.insertTable')}>
      <OmFormItem
        name="inserttable"
        className={classNames(styles.iconCheckbox, styles.notCheckbox, styles.insertTable)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarInsertTable} alt="toolbar-insert-table" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getLinkElem(styles: StylesType): React.JSX.Element {
  defaultValues.link = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.link')}>
      <OmFormItem
        name="link"
        className={classNames(styles.iconCheckbox, styles.notCheckbox, styles.link)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarLink} alt="toolbar-link" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getQuoteElem(styles: StylesType): React.JSX.Element {
  defaultValues.blockquote = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.quote')}>
      <OmFormItem
        name="blockquote"
        className={classNames(styles.iconCheckbox, styles.quote)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarQuote} alt="toolbar-quote" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getLtrElem(styles: StylesType): React.JSX.Element {
  defaultValues.ltr = false
  return (
    <OmTooltip title={i18n.t('editor.tooltip.ltr')}>
      <OmFormItem
        name="ltr"
        className={classNames(styles.iconCheckbox, styles.ltr)}
        valuePropName="checked"
      >
        <OmCheckbox>
          <img src={toolbarLtr} alt="toolbar-ltr" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getRtlElem(styles: StylesType): React.JSX.Element {
  defaultValues.rtl = false
  const attrs = { tabIndex: -1 }
  return (
    <OmTooltip title={i18n.t('editor.tooltip.rtl')}>
      <OmFormItem
        name="rtl"
        className={classNames(styles.iconCheckbox, styles.rtl)}
        valuePropName="checked"
      >
        <OmCheckbox {...attrs}>
          <img src={toolbarRtl} alt="toolbar-rtl" />
        </OmCheckbox>
      </OmFormItem>
    </OmTooltip>
  )
}

export function getToolbarElems(styles: StylesType, _values?: any): React.JSX.Element {
  return (
    <React.Fragment>
      {getFontFamilyElem(styles)}
      {getFontSizeElem(styles)}
      {getTextColorElem(styles)}
      {getBackColorElem(styles)}
      {getBoldElem(styles)}
      {getItalicElem(styles)}
      {getStrikeElem(styles)}
      {getUnderlineElem(styles)}
      {getClearElem(styles)}
      <div className={classNames(styles.divider)} />
      {getNumberedListElem(styles)}
      {getBulletedListElem(styles)}
      {getIndentIncreaseElem(styles)}
      {getIndentDecreaseElem(styles)}
      {getLineElem(styles)}
      <div className={classNames(styles.divider)} />
      {getAlignLeftElem(styles)}
      {getAlignCenterElem(styles)}
      {getAlignRightElem(styles)}
      <div className={classNames(styles.divider)} />
      {getInsertImageElem(styles)}
      {getInsertTableElem(styles)}
      {getLinkElem(styles)}
      {getQuoteElem(styles)}
      <div className={classNames(styles.divider)} />
      {getLtrElem(styles)}
      {getRtlElem(styles)}
    </React.Fragment>
  )
}

function getMenuItem(key: string, elem: React.JSX.Element): OmMenuItem {
  return {
    label: elem,
    key,
  }
}

function getDivider(styles: StylesType, key: string): OmMenuItem {
  return {
    label: <div className={classNames(styles.divider)} />,
    key,
    className: classNames(styles.dividerWrapper),
  }
}

export function getToolbarMenu(styles: StylesType, _values?: any): React.JSX.Element {
  const menuItems: OmMenuItem[] = [
    getDivider(styles, 'divider0'),
    getMenuItem('fontFamily', getFontFamilyElem(styles)),
    getMenuItem('fontSize', getFontSizeElem(styles)),
    getMenuItem('textColor', getTextColorElem(styles)),
    getMenuItem('backColor', getBackColorElem(styles)),
    getMenuItem('bold', getBoldElem(styles)),
    getMenuItem('italic', getItalicElem(styles)),
    getMenuItem('strike', getStrikeElem(styles)),
    getMenuItem('underline', getUnderlineElem(styles)),
    getMenuItem('clear', getClearElem(styles)),
    getDivider(styles, 'divider1'),
    getMenuItem('numberedList', getNumberedListElem(styles)),
    getMenuItem('bulletedList', getBulletedListElem(styles)),
    getMenuItem('indentIncrease', getIndentIncreaseElem(styles)),
    getMenuItem('indentDecrease', getIndentDecreaseElem(styles)),
    getMenuItem('line', getLineElem(styles)),
    getDivider(styles, 'divider2'),
    getMenuItem('alignLeft', getAlignLeftElem(styles)),
    getMenuItem('alignCenter', getAlignCenterElem(styles)),
    getMenuItem('alignRight', getAlignRightElem(styles)),
    getDivider(styles, 'divider3'),
    getMenuItem('insertImage', getInsertImageElem(styles)),
    getMenuItem('insertTable', getInsertTableElem(styles)),
    getMenuItem('link', getLinkElem(styles)),
    getMenuItem('quote', getQuoteElem(styles)),
    getDivider(styles, 'divider4'),
    getMenuItem('ltr', getLtrElem(styles)),
    getMenuItem('rtl', getRtlElem(styles)),
  ]

  const MoreMenu: React.FC<MoreMenuProps> = (props) => {
    const { items } = props

    const menuRef = useRef<OmMenuRef>(null)

    const attrs: OmMenuProps = {
      items,
      mode: 'horizontal',
      overflowedIndicatorPopupClassName: classNames(
        styles.editorToolbarPopup,
        styles.editorToolbarContainer,
        'editorToolbarPopup',
      ),
      triggerSubMenuAction: 'click',
      // "더보기"(overflow rest)는 유일한 서브메뉴 → openKeys 존재 = 열림(MailToolbar 선례).
      onOpenChange: (openKeys) => {
        if (openKeys.length > 0) {
          const moreItem = menuRef.current?.getOverflowRestElement()
          if (moreItem != null) {
            const rect = moreItem.getBoundingClientRect()
            const fullHeight = document.documentElement.clientHeight

            const topH = rect.top
            const bottomH = fullHeight - rect.bottom

            const maxH = Math.max(topH, bottomH) - 20

            document.documentElement.style.setProperty(
              '--editor-toolbar-more-menu-height',
              `${maxH}px`,
            )
          }
        } else {
          document.documentElement.style.removeProperty('--editor-toolbar-more-menu-height')
        }
      },
    }

    return <OmMenu ref={menuRef} {...attrs} />
  }

  return <MoreMenu items={menuItems} />
}

export function getDefaultValues(): DefaultValuesType {
  return defaultValues
}

function urlValidator(value: string, t: typeof i18n.t): Promise<void> {
  if (value == null || value.length === 0) {
    return Promise.resolve()
  }
  if (value != null && value.length > 0) {
    const urlRegex =
      /^((https?:\/\/){0,1}|(www\.){0,1})([-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b){1}((\/{1}[-a-zA-Z0-9()=._]*)*)(#|\?|$){1}([-a-zA-Z0-9()@:%_\+.\?#,~&//=]*)$/i
    const isURL = urlRegex.test(value)

    if (isURL === true) {
      return Promise.resolve()
    }
  }
  return Promise.reject(new Error(t('editor.warning.invalidURL')))
}

export function insertLink(url: string | null, text: string, type: string, apis: EditorApis): void {
  const tmpObj: {
    title: string
    ref: React.RefObject<OmFormInstance>
    btnDisabled: boolean
    display: string
    url: string | null
    modalObj?: ModalObj
    delete: () => void
    setMoveBtnState: (config: { disabled: boolean }) => void
    render: () => React.JSX.Element
    close: () => void
    updateModal: () => void
  } = {
    title: i18n.t('editor.title.insertLink'),
    ref: React.createRef(),
    btnDisabled: true,
    display: text,
    url: url != null && url.trim().length > 0 ? url : null,
    delete: () => {
      apis.removeLink()
      tmpObj.close()
    },
    setMoveBtnState: undefined as any,
    render: undefined as any,
    close: undefined as any,
    updateModal: undefined as any,
  }

  tmpObj.setMoveBtnState = ({ disabled }: { disabled: boolean }) => {
    tmpObj.btnDisabled = disabled
    tmpObj.updateModal()
  }

  tmpObj.render = () => {
    const formAttrs = {
      className: classNames(styles0.inserLinkForm),
      layout: 'vertical' as const,
      ref: tmpObj.ref,
      requiredMark: false,
      initialValues: {
        display: tmpObj.display,
        url: tmpObj.url,
      },
      onValuesChange: (_changed: Record<string, unknown>, all: Record<string, unknown>) => {
        const values = all as unknown as InsertLinkValues
        if (
          values.display != null &&
          values.display.trim().length > 0 &&
          values.url != null &&
          values.url.trim().length > 0
        ) {
          tmpObj.setMoveBtnState({ disabled: false })
        } else {
          tmpObj.setMoveBtnState({ disabled: true })
        }
      },
    }

    const displayRules: OmRule[] = [
      { required: true, message: i18n.t('editor.rule.requiredDisplay') },
    ]

    const urlRules: OmRule[] = [
      { required: true, message: i18n.t('editor.rule.requiredURL') },
      { validator: (_rule, value) => urlValidator(value as string, i18n.t) },
    ]

    const displayHidden = type !== 'text'

    return (
      <OmForm {...formAttrs}>
        <OmFormItem
          name="display"
          hidden={displayHidden}
          label={i18n.t('editor.fields.displayName')}
          rules={displayRules}
        >
          <OmInput />
        </OmFormItem>
        <OmFormItem name="url" label={i18n.t('editor.fields.webAddress')} rules={urlRules}>
          <OmInput />
        </OmFormItem>
      </OmForm>
    )
  }

  tmpObj.modalObj = confirmBase({
    wrapClassName: classNames(styles0.insertLinkPopup),
    title: tmpObj.title,
    content: tmpObj.render(),
    okText: i18n.t('common.button.ok'),
    okButtonProps: {
      className: classNames('type1', 'h38', 'w100'),
      disabled: tmpObj.btnDisabled,
    },
    onOk: (closeFunc: () => void) => {
      closeFunc()
      const values = tmpObj.ref.current!.getFieldsValue() as unknown as InsertLinkValues
      console.log('addLink - ', values)
      apis.addLink(values.url, values.display)
    },
    afterClose: () => {
      // tmpObj.dragPopup.removeEvent();
      apis.focus()
    },
    footer: (_: any, { OkBtn, CancelBtn }: any) => (
      <div className={classNames(styles0.footer)}>
        <div className={classNames('btns')}>
          {tmpObj.url && (
            <OmButton
              className={classNames('delete', 'h38')}
              onClick={() => {
                tmpObj.delete()
              }}
            >
              {i18n.t('editor.button.removeLink')}
            </OmButton>
          )}
        </div>
        <div className={classNames('btns')}>
          <CancelBtn />
          <OkBtn />
        </div>
      </div>
    ),
  })

  tmpObj.close = () => {
    tmpObj.modalObj!.destroy()
  }

  // tmpObj.dragPopup = dragPopup(tmpObj);

  tmpObj.updateModal = () => {
    tmpObj.modalObj!.updateBase?.({
      content: tmpObj.render(),
      okButtonProps: {
        className: classNames('type1', 'h38', 'w100'),
        disabled: tmpObj.btnDisabled,
      },
    })
  }
}

const ext = {
  getFontFamilyElem,
  getFontSizeElem,
  getTextColorElem,
  getBackColorElem,
  getBoldElem,
  getItalicElem,
  getStrikeElem,
  getUnderlineElem,
  getClearElem,
  getNumberedListElem,
  getBulletedListElem,
  getIndentIncreaseElem,
  getIndentDecreaseElem,
  getLineElem,
  getAlignLeftElem,
  getAlignCenterElem,
  getAlignRightElem,
  getInsertImageElem,
  getLinkElem,
  getQuoteElem,
  getLtrElem,
  getRtlElem,
  getToolbarElems,
  getToolbarMenu,
  getDefaultValues,
  insertLink,
}

export default ext
